// Input management module for PPT Hero game
export default class InputManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Handle keyboard input
  handleKeyDown(event) {
    try {
      // Track keystrokes
      this.scene.trackKeystrokes();
      
      // Handle shortcuts
      this.scene.handleShortcuts(event);
      
      // Handle special keys
      if (event.key === 'Escape') {
        this.handleEscapeKey();
      } else if (event.key === 'Enter') {
        this.handleEnterKey();
      } else if (event.key === ' ') {
        this.handleSpaceKey();
      }
      
    } catch (error) {
      console.error('Error handling key down:', error);
    }
  }

  // Handle escape key
  handleEscapeKey() {
    if (this.scene.isTransitioning) return;
    
    // Clear selection
    this.scene.clearSelection();
    
    // Hide any active popups
    this.scene.hideInstructionsOverlay();
    
    // Show notification
    this.scene.showCenterNotification('Selection cleared', 'info', 1000);
  }

  // Handle enter key
  handleEnterKey() {
    if (this.scene.isTransitioning) return;
    
    // Start level if ready
    if (this.scene.currentRound && !this.scene.currentLevel) {
      this.scene.startLevel(this.scene.currentRoundData.levels[0]);
    }
  }

  // Handle space key
  handleSpaceKey() {
    if (this.scene.isTransitioning) return;
    
    // Toggle pause/resume
    if (this.scene.gameState === 'playing') {
      this.scene.gameState = 'paused';
      this.scene.showCenterNotification('Game paused', 'warning', 1500);
    } else if (this.scene.gameState === 'paused') {
      this.scene.gameState = 'playing';
      this.scene.showCenterNotification('Game resumed', 'success', 1500);
    }
  }

  // Handle pointer down events
  onPointerDown(pointer, objectsUnderPointer) {
    if (this.scene.isTransitioning) return;
    
    try {
      // Track analytics
      if (this.scene.levelClicks !== undefined) this.scene.levelClicks++;
      
      // Initialize analyticsData if it doesn't exist
      if (!this.scene.analyticsData) {
        this.scene.analyticsData = {
          levelStartTime: Date.now() / 1000,
          shortcutAttempts: 0,
          successfulShortcuts: 0,
          mouseClicks: 0,
          keystrokeCount: 0,
          errors: {
            misstypedShortcuts: 0,
            incorrectSequences: 0,
            missclicks: 0
          },
          shortcutsUsed: {},
          shortcutsMissed: {}
        };
      }
      
      // Track mouse clicks
      this.scene.analyticsData.mouseClicks++;
      
      // Filter out fixed and decorative elements
      const clickableObjects = [];
      for (let i = 0; i < objectsUnderPointer.length; i++) {
        const obj = objectsUnderPointer[i];
        if (!obj.getData || (!obj.getData('isFixed') && !obj.getData('isDecoration'))) {
          clickableObjects.push(obj);
        }
      }

      if (clickableObjects.length === 0) {
        // Clicked empty space - potentially a missclick
        if (!this.scene.isMarqueeSelecting && this.scene.selectedShapes.length > 0) {
          this.scene.analyticsData.errors.missclicks++;
        }

        // Start marquee selection
        if (!pointer.event.shiftKey && !pointer.event.ctrlKey) {
          this.scene.clearSelection();
        }
        this.scene.isMarqueeSelecting = true;
        this.scene.marqueeWithModifier = pointer.event.shiftKey || pointer.event.ctrlKey;
        this.scene.marqueeStartX = pointer.x;
        this.scene.marqueeStartY = pointer.y;
        // Add null checks to prevent errors during scene transitions
        if (this.scene.marqueeRect) {
          this.scene.marqueeRect.setPosition(pointer.x, pointer.y);
          this.scene.marqueeRect.setSize(0, 0);
          this.scene.marqueeRect.setVisible(true);
        }
      } else {
        // Clicked on shape(s) - Let individual shape handlers deal with selection
        // DO NOT call stopPropagation() - let shape's own pointerdown handlers run
        console.log('🔍 InputManager: Click on shape detected - delegating to shape handlers');
        
        // Only handle non-shape objects here if needed
        const topObj = clickableObjects[clickableObjects.length - 1];
        if (!this.scene.allShapes.includes(topObj)) {
          // Clicked on non-shape object - handle normally
          return;
        }
        
        // For shape objects: DO NOTHING - let ShapeManager handle via individual shape.on('pointerdown') handlers
        // This eliminates the conflict between InputManager and ShapeManager selection systems
      }
      
    } catch (error) {
      console.error('Error handling pointer down:', error);
    }
  }

  // Handle pointer move events
  onPointerMove(pointer) {
    if (this.scene.isTransitioning) return;
    
    try {
      if (this.scene.isMarqueeSelecting) {
        const x = Math.min(this.scene.marqueeStartX, pointer.x);
        const y = Math.min(this.scene.marqueeStartY, pointer.y);
        const w = Math.abs(pointer.x - this.scene.marqueeStartX);
        const h = Math.abs(pointer.y - this.scene.marqueeStartY);
        // Add null checks to prevent errors during scene transitions
        if (this.scene.marqueeRect) {
          this.scene.marqueeRect.setPosition(x, y);
          this.scene.marqueeRect.setSize(w, h);
        }
      }
      
      // Update cursor based on what's under the pointer
      this.scene.shapeManager.onPointerMove(pointer);
      
    } catch (error) {
      console.error('Error handling pointer move:', error);
    }
  }

  // Handle pointer up events
  onPointerUp() {
    if (this.scene.isTransitioning) return;
    
    try {
      if (this.scene.isMarqueeSelecting) {
        this.scene.isMarqueeSelecting = false;
        const mx = this.scene.marqueeRect.x;
        const my = this.scene.marqueeRect.y;
        const mw = this.scene.marqueeRect.displayWidth;
        const mh = this.scene.marqueeRect.displayHeight;

        this.scene.allShapes.forEach((shape) => {
          const halfW = shape.displayWidth / 2;
          const halfH = shape.displayHeight / 2;
          const left = shape.x - halfW;
          const right = shape.x + halfW;
          const top = shape.y - halfH;
          const bottom = shape.y + halfH;
          if (right > mx && left < mx + mw && bottom > my && top < my + mh) {
            if (this.scene.marqueeWithModifier) {
              // Ctrl/Shift marquee => toggle selection of shapes in area
              if (this.scene.selectedShapes.includes(shape)) {
                this.scene.deselectShape(shape);
              } else {
                this.scene.addToSelection(shape);
              }
            } else {
              // Normal marquee => add all shapes in area
              this.scene.addToSelection(shape);
            }
          }
        });

        this.scene.marqueeRect.setVisible(false);
        this.scene.marqueeWithModifier = false; // Reset modifier flag
      }
      
      // Reset cursor
      this.scene.shapeManager.onPointerUp();
      
    } catch (error) {
      console.error('Error handling pointer up:', error);
    }
  }

  // Handle drag events
  onDragStart(pointer, gameObject) {
    if (this.scene.isTransitioning) return;
    
    try {
      this.scene.shapeManager.onDragStart(pointer, gameObject);
    } catch (error) {
      console.error('Error handling drag start:', error);
    }
  }

  onDrag(pointer, gameObject, dragX, dragY) {
    if (this.scene.isTransitioning) return;
    
    try {
      this.scene.shapeManager.onDrag(pointer, gameObject, dragX, dragY);
    } catch (error) {
      console.error('Error handling drag:', error);
    }
  }

  onDragEnd(pointer, gameObject) {
    if (this.scene.isTransitioning) return;
    
    try {
      this.scene.shapeManager.onDragEnd(pointer, gameObject);
    } catch (error) {
      console.error('Error handling drag end:', error);
    }
  }

  // Enable input
  enableInput() {
    this.scene.inputEnabled = true;
    this.scene.input.setDefaultCursor('default');
  }

  // Disable input
  disableInput() {
    this.scene.inputEnabled = false;
    this.scene.input.setDefaultCursor('default');
  }

  // Check if input is enabled
  isInputEnabled() {
    return this.scene.inputEnabled;
  }
}



